import { z } from 'zod';

export const REVENUECAT_ADJUST_TRACKING_CONSENT_ATTRIBUTE = 'adjustTrackingConsent';

const BaseRevenueCatWebhookEvent = z.object({
  app_id: z.string().optional(),
  environment: z.enum(['SANDBOX', 'PRODUCTION']),
  store: z.enum(['APP_STORE', 'PLAY_STORE', 'AMAZON', 'MAC_APP_STORE', 'STRIPE', 'PROMOTIONAL', 'ROKU']),
  event_timestamp_ms: z.number(),
  id: z.string(),
  aliases: z.array(z.string()).nullish(),
  subscriber_attributes: z.record(
    z.string(),
    z
      .object({
        updated_at_ms: z.number(),
        value: z.string(),
      })
      .optional(),
  ),
});

const RevenueCatWebhookTransferEvent = BaseRevenueCatWebhookEvent.extend({
  type: z.enum(['TRANSFER']),
  transferred_from: z.array(z.string()),
  transferred_to: z.array(z.string()),
});

export const RevenueCatCancellationReason = z.enum([
  'UNSUBSCRIBE',
  'BILLING_ERROR',
  'DEVELOPER_INITIATED',
  'PRICE_INCREASE',
  'CUSTOMER_SUPPORT',
  'UNKNOWN',
]);

export const RevenueCatWebhookLifecycleEvent = BaseRevenueCatWebhookEvent.extend({
  type: z.enum([
    'INITIAL_PURCHASE',
    'RENEWAL',
    'CANCELLATION',
    'UNCANCELLATION',
    'NON_RENEWING_PURCHASE',
    'SUBSCRIPTION_PAUSED',
    'EXPIRATION',
    'BILLING_ISSUE',
    'PRODUCT_CHANGE',
    'SUBSCRIPTION_EXTENDED',
    'TEMPORARY_ENTITLEMENT_GRANT',
    'TEST',
  ]),
  original_app_user_id: z.string().nullish(),
  app_user_id: z.string(),
  product_id: z.string(),
  entitlement_id: z.string().nullish(),
  entitlement_ids: z.array(z.string()).nullish(),
  period_type: z.string().nullish(),
  purchased_at_ms: z.number(),
  expiration_at_ms: z.number().nullish(),
  // BILLING_ISSUE: when the store's billing grace period ends; null when the subscription has no grace period.
  grace_period_expiration_at_ms: z.number().nullish(),
  presented_offering_id: z.string().nullish(),
  offer_code: z.string().nullish(),
  price: z.number().nullish(),
  currency: z.string(),
  price_in_purchased_currency: z.number(),
  tax_percentage: z.number().nullish(),
  commission_percentage: z.number().nullish(),
  takehome_percentage: z.number().nullish(),
  is_trial_conversion: z.boolean().nullish(),
  transaction_id: z.string(),
  original_transaction_id: z.string(),
  is_family_share: z.boolean().nullish(),
  country_code: z.string().nullish(),
  cancel_reason: RevenueCatCancellationReason.nullish(),
  expiration_reason: z.string().nullish(),
  // PRODUCT_CHANGE: the product the subscription is changing to.
  new_product_id: z.string().nullish(),
  // SUBSCRIPTION_PAUSED: when the paused subscription is scheduled to auto-resume.
  auto_resume_at_ms: z.number().nullish(),
});

export type RevenueCatCancellationReason = z.infer<typeof RevenueCatCancellationReason>;
export type RevenueCatWebhookLifecycleEvent = z.infer<typeof RevenueCatWebhookLifecycleEvent>;
export type RevenueCatWebhookTransferEvent = z.infer<typeof RevenueCatWebhookTransferEvent>;

const RevenueCatWebhookEvent = z.discriminatedUnion('type', [
  RevenueCatWebhookLifecycleEvent,
  RevenueCatWebhookTransferEvent,
]);

export type RevenueCatWebhookEvent = z.infer<typeof RevenueCatWebhookEvent>;

export const RevenueCatWebhookBody = z.object({
  event: RevenueCatWebhookEvent,
  api_version: z.string(),
});

export type RevenueCatWebhookBody = z.infer<typeof RevenueCatWebhookBody>;
